
import React, { useState } from "react";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { currencyConverter, EXCHANGE_PROVIDERS, ExchangeProvider } from "@/services/currencyConverter";
import { useCurrencies } from "@/hooks/useCurrencies";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { toast } from "sonner";

export function ExchangeRateStep() {
  const { useCurrenciesQuery } = useCurrencies();
  const { data: currencies = [], isLoading: isLoadingCurrencies } = useCurrenciesQuery();
  const { preferences, updatePreferences, isLoading } = useUserPreferences();
  
  const [provider, setProvider] = useState<ExchangeProvider>(
    (preferences?.exchange_provider as ExchangeProvider) || "exchange-api"
  );
  const [apiKey, setApiKey] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const selectedProvider = EXCHANGE_PROVIDERS[provider];
  const apiKeyRequired = selectedProvider?.apiKeyRequired;
  
  const handleSaveProvider = async () => {
    if (provider === preferences?.exchange_provider && !apiKey) {
      return;
    }
    
    setIsSaving(true);
    try {
      // Update provider in user preferences
      const updateData: any = { exchange_provider: provider };
      
      // If API key is provided, store it too
      if (apiKey && apiKeyRequired) {
        updateData[`${provider}_api_key`] = apiKey;
      }
      
      await updatePreferences(updateData);
      
      // Configure the converter with new settings
      currencyConverter.setProvider(provider);
      if (apiKey && apiKeyRequired) {
        await currencyConverter.setApiKey(provider, apiKey);
      }
      
      toast.success("Exchange rate provider settings saved successfully");
    } catch (error) {
      console.error("Error saving exchange provider settings:", error);
      toast.error("Failed to save exchange rate provider settings");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <p>
        Exchange rates are used to convert contract amounts between different currencies.
        Choose the provider you'd like to use for exchange rate data.
      </p>
      
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Exchange Rate Provider</CardTitle>
          <CardDescription>
            Select which service to use for currency conversion
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="provider">Provider</Label>
            <Select
              value={provider}
              onValueChange={(value) => setProvider(value as ExchangeProvider)}
              disabled={isSaving}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a provider" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(EXCHANGE_PROVIDERS).map(([id, info]) => (
                  <SelectItem key={id} value={id}>
                    <div className="flex items-center justify-between w-full">
                      <span>{info.name}</span>
                      {id === "exchange-api" && (
                        <Badge variant="outline" className="ml-2 bg-green-50 text-green-700">Free</Badge>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {selectedProvider && (
            <div className="text-sm text-muted-foreground">
              <p>{selectedProvider.description}</p>
              {selectedProvider.apiKeyRequired && (
                <p className="mt-1">
                  <a 
                    href={selectedProvider.apiKeyUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Get an API key
                  </a>
                </p>
              )}
            </div>
          )}
          
          {apiKeyRequired && (
            <div className="space-y-2 mt-4">
              <Label htmlFor="apiKey">API Key</Label>
              <Input
                id="apiKey"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter your API key"
                disabled={isSaving}
              />
            </div>
          )}
          
          <Button
            className="mt-4"
            onClick={handleSaveProvider}
            disabled={isSaving || (apiKeyRequired && !apiKey)}
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>Save Provider</>
            )}
          </Button>
        </CardContent>
      </Card>
      
      <p className="text-sm text-muted-foreground">
        The free Exchange API option works well for most users, but if you need more
        frequent updates or specific currency pairs, you may want to use a premium service.
      </p>
    </div>
  );
}
